import { React } from 'Imports';
import { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faChevronDown, faChevronUp, faXmark, faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons';
import { GroupDto } from '$Generated/api';

const styles = require('./MultiGroupSelect.scss') as {
    multiSelectOptionWrapper: string;
    multiSelect: string;
    multiSelectContainer: string;
    multiSelectHeader: string;
    multiSelectDropdown: string;
    multiSelectSelected: string;
    multiSelectPlaceholder: string;
    multiSelectTags: string;
    multiSelectTag: string;
    multiSelectTagRemove: string;
    multiSelectActions: string;
    multiSelectClear: string;
    multiSelectArrow: string;
    multiSelectSearch: string;
    multiSelectOptions: string;
    multiSelectOption: string;
    multiSelectNoOptions: string;
    multiSelectCheckbox: string;
    multiSelectOptionLabel: string;
    selected: string;
    multiSelectGroupIcon: string;
    multiSelectChildren: string;
    multiSelectSearchClear: string;
};

export type Option = {
    id: string;
    label: string;
};

type MultiGroupSelectProps = {
    options: GroupDto[];
    placeholder?: string;
    onChange?: (selectedIds: string[]) => void;
    selected?: string[];
};

const MultiGroupSelect: React.FC<MultiGroupSelectProps> = ({ options, placeholder = 'Select items...', onChange, selected = [] }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState<string[]>(selected);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setSelectedIds(selected);
    }, [selected]);

    const findItemById = (id: string, items: GroupDto[]): GroupDto | null => {
        for (const item of items) {
            if (item.id === id) return item;
            if (item.children) {
                const found = findItemById(id, item.children);
                if (found) return found;
            }
        }
        return null;
    };

    const getItemLevel = (id: string, items: GroupDto[], level = 0): number => {
        for (const item of items) {
            if (item.id === id) return level;
            if (item.children) {
                const childLevel = getItemLevel(id, item.children, level + 1);
                if (childLevel !== -1) return childLevel;
            }
        }
        return -1;
    };

    const findTopLevelParent = (id: string, items: GroupDto[]): GroupDto | null => {
        for (const item of items) {
            if (item.id === id) return item;
            if (item.children) {
                const found = findItemById(id, item.children);
                if (found) return item;
            }
        }
        return null;
    };

    const getAllItems = (items: GroupDto[]): GroupDto[] => {
        const result: GroupDto[] = [];
        items.forEach((item) => {
            result.push(item);
            if (item.children) {
                result.push(...getAllItems(item.children));
            }
        });
        return result;
    };

    const matchesSearch = (item: GroupDto): boolean => {
        return item.name?.toLowerCase().includes(searchTerm.toLowerCase()) || false;
    };

    const hasMatchingChildren = (item: GroupDto): boolean => {
        if (!item.children) return false;
        return item.children.some((child) => matchesSearch(child) || hasMatchingChildren(child));
    };

    const shouldShowItem = (item: GroupDto): boolean => {
        if (searchTerm === '') return true;
        return matchesSearch(item) || hasMatchingChildren(item);
    };

    useEffect(() => {
        if (searchTerm) {
            const groupsToExpand = new Set<string>();

            const findMatchingGroups = (items: GroupDto[], parentMatches: boolean = false) => {
                items.forEach((item) => {
                    const currentItemMatches = matchesSearch(item) || parentMatches;

                    if (item.children) {
                        if (currentItemMatches || hasMatchingChildren(item)) {
                            if (item.id) groupsToExpand.add(item.id);
                        }
                        findMatchingGroups(item.children, currentItemMatches);
                    }
                });
            };

            findMatchingGroups(options);
            setExpandedGroups(Array.from(groupsToExpand));
        }
    }, [searchTerm, options]);

    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, []);

    const toggleGroup = (event: React.MouseEvent, groupId?: string) => {
        event.stopPropagation();
        if (!groupId) return;
        setExpandedGroups((prev) => (prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]));
    };

    const findParentChain = (targetId: string, items: GroupDto[], chain: GroupDto[] = []): GroupDto[] => {
        for (const item of items) {
            if (item.id === targetId) {
                return chain;
            }
            if (item.children) {
                const result = findParentChain(targetId, item.children, [...chain, item]);
                if (result.length > 0) return result;
            }
        }
        return [];
    };

    const getAllDescendants = (group: GroupDto): string[] => {
        const descendants: string[] = [];
        if (group.children) {
            group.children.forEach((child) => {
                if (child.id) descendants.push(child.id);
                descendants.push(...getAllDescendants(child));
            });
        }
        return descendants;
    };

    const toggleOption = (event: React.MouseEvent, option: GroupDto) => {
        event.stopPropagation();
        if (!option.id) return;

        let newSelectedIds = [...selectedIds];
        const isSelected = selectedIds.includes(option.id);
        const optionLevel = getItemLevel(option.id, options);
        const topLevelParent = findTopLevelParent(option.id, options);

        if (isSelected) {
            // Remove this option and its descendants
            newSelectedIds = newSelectedIds.filter((id) => id !== option.id && !getAllDescendants(option).includes(id));
        } else {
            if (topLevelParent) {
                const parentChain = findParentChain(option.id, [topLevelParent]);

                // Only remove selections within the same top-level hierarchy
                newSelectedIds = newSelectedIds.filter((id) => {
                    const itemTopParent = findTopLevelParent(id, options);
                    if (itemTopParent?.id !== topLevelParent.id) return true;

                    const itemLevel = getItemLevel(id, [topLevelParent]);
                    return itemLevel === optionLevel;
                });

                // Remove any selected parents in the chain
                newSelectedIds = newSelectedIds.filter((id) => !parentChain.some((parent) => parent.id === id));

                // Remove any selected descendants
                newSelectedIds = newSelectedIds.filter((id) => !getAllDescendants(option).includes(id));
            }

            // Add the new option
            newSelectedIds.push(option.id);
        }

        setSelectedIds(newSelectedIds);
        if (onChange) {
            onChange(newSelectedIds);
        }
    };

    const removeItem = (event: React.MouseEvent, optionId: string) => {
        event.stopPropagation();
        const newSelectedIds = selectedIds.filter((id) => id !== optionId);
        setSelectedIds(newSelectedIds);

        if (onChange) {
            onChange(newSelectedIds);
        }
    };

    const clearAll = (event: React.MouseEvent) => {
        event.stopPropagation();
        setSelectedIds([]);

        if (onChange) {
            onChange([]);
        }
    };

    const clearSearch = (event: React.MouseEvent) => {
        event.stopPropagation();
        setSearchTerm('');
        setExpandedGroups([]);
    };

    const renderOption = (option: GroupDto, level: number = 0) => {
        if (!shouldShowItem(option)) return null;
        const isSelected = selectedIds.includes(option.id ?? '');
        const padding = level == 0 ? '0px' : `${24}px`;
        const hasChildren = option.children && option.children.length > 0;
        const isExpanded = hasChildren && expandedGroups.includes(option.id || '');
        return (
            <div key={option.id} className={`${styles.multiSelectOptionWrapper}`} style={{ paddingLeft: `${padding}` }}>
                <div className={`${styles.multiSelectOption} ${isSelected ? styles.selected : ''}`}>
                    <div className={styles.multiSelectCheckbox} onClick={(e) => toggleOption(e, option)}>
                        {isSelected && <FontAwesomeIcon icon={faCheck} size="sm" />}
                    </div>
                    <div className={styles.multiSelectOptionLabel}>
                        {option.name} - ({option.id})
                    </div>
                    {hasChildren && (
                        <div className={styles.multiSelectGroupIcon} onClick={(e) => toggleGroup(e, option.id)}>
                            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                        </div>
                    )}
                </div>
                {hasChildren && isExpanded && (
                    <div className={styles.multiSelectChildren}>{option.children?.map((child) => renderOption(child, level + 1))}</div>
                )}
            </div>
        );
    };

    const getSelectedItems = (): GroupDto[] => {
        return selectedIds.map((id) => findItemById(id, options)).filter((item): item is GroupDto => item !== null);
    };

    return (
        <div className={styles.multiSelectContainer} ref={containerRef}>
            <div className={styles.multiSelectHeader} onClick={() => setIsOpen(!isOpen)}>
                <div className={styles.multiSelectSelected}>
                    {selectedIds.length === 0 ? (
                        <span className={styles.multiSelectPlaceholder}>{placeholder}</span>
                    ) : (
                        <div className={styles.multiSelectTags}>
                            {getSelectedItems().map((option) => (
                                <div key={option.id} className={styles.multiSelectTag}>
                                    <span>{option.name}</span>
                                    <button className={styles.multiSelectTagRemove} onClick={(e) => removeItem(e, option.id ?? '')}>
                                        <FontAwesomeIcon icon={faXmark} size="sm" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className={styles.multiSelectActions}>
                    {selectedIds.length > 0 && (
                        <button className={styles.multiSelectClear} onClick={clearAll}>
                            <FontAwesomeIcon icon={faXmark} />
                        </button>
                    )}
                    <div className={styles.multiSelectArrow}>
                        <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
                    </div>
                </div>
            </div>
            {isOpen && (
                <div className={styles.multiSelectDropdown}>
                    <div className={styles.multiSelectSearch}>
                        <FontAwesomeIcon icon={faMagnifyingGlass} />
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                        />
                        {searchTerm && (
                            <button className={styles.multiSelectSearchClear} onClick={clearSearch}>
                                <FontAwesomeIcon icon={faXmark} />
                            </button>
                        )}
                    </div>
                    <div className={styles.multiSelectOptions}>
                        {options.some((option) => shouldShowItem(option)) ? (
                            options.map((option) => renderOption(option))
                        ) : (
                            <div className={styles.multiSelectNoOptions}>No options found</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MultiGroupSelect;
